import type { ContentReferenceContext } from '@gitbook/app-database';
import { annotation, buttons, contentRef, INLINE_LINK } from '@gitbook/doc-core';
import type { HiveClient } from '@gitbook/hive-client';
import {
    type PaletteHandle,
    PalettePositionMaxHeight,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { isBlockRangeSelection, type TextRange, type Value } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { isUrlLike } from '@gitbook/util-url';

import { createInputContentRefPalette } from '../../ContentReferences';
import { highlightSelection } from '../common';

export const toggleMark =
    ({ type, editor }: { type: string; editor: EditorContainer }) =>
    () => {
        editor.change((change) => {
            change.toggleMark(type);
            change.focus();
        });
    };

/*
 * Click to insert a link/mention
 */
export function toggleLink(props: {
    palette: PaletteHandle;
    editor: EditorContainer;
    contentReferenceContext: ContentReferenceContext;
    isInLink: boolean;
    hiveClient: HiveClient;
    insertAtRange?: TextRange;
}) {
    const { palette, editor, contentReferenceContext, isInLink, hiveClient, insertAtRange } = props;
    return () => {
        // If we are in a link, we remove it
        if (isInLink) {
            editor.change((change) => {
                change
                    .unwrapInline({
                        type: INLINE_LINK,
                    })
                    .focus();
            });
            return;
        }

        editor.change((change) => {
            highlightSelection(change);
        });

        const contentRefPaletteDefinition = createInputContentRefPalette(
            (ref) => {
                editor.change((c) => {
                    insertAtRange
                        ? contentRef.insertContentReferenceAtRange(c, ref, insertAtRange)
                        : contentRef.insertContentReference(c, ref);
                });
            },
            {
                ...contentReferenceContext,
                document: editor.value.document,
            },
            hiveClient,
            {
                ...(isSelectionUrlLike(editor.value)
                    ? {
                          search: {
                              initialValue: editor.value.fragment.getText(),
                          },
                      }
                    : {}),
                position: {
                    preferredPlacement: 'left',
                    preferredAlignment: 'middle',
                    width: PalettePositionWidth.LINKS,
                    maxHeight: PalettePositionMaxHeight.AUTO,
                },
            }
        );

        palette.open(contentRefPaletteDefinition);
        /**
         * Also return the definition if we want to use it in a command
         * trigger.
         */
        return contentRefPaletteDefinition;
    };
}

/*
 * Click to insert a button.
 */
export const toggleButton =
    ({
        palette,
        editor,
        contentReferenceContext,
        hiveClient,
        range,
    }: {
        palette: PaletteHandle;
        editor: EditorContainer;
        contentReferenceContext: ContentReferenceContext;
        hiveClient: HiveClient;
        range?: TextRange;
    }) =>
    () => {
        editor.change((change) => {
            highlightSelection(change);
        });

        const contentRefPaletteDefinition = createInputContentRefPalette(
            (ref) => {
                editor.change((change) => {
                    if (isBlockRangeSelection(change.value.selection)) {
                        return change;
                    }

                    const textRange = range ?? change.value.textSelection;
                    change
                        .insertInlineAtRange(
                            textRange,
                            buttons.createInlineButton({
                                ref,
                            })
                        )
                        .focus();
                });
            },
            {
                ...contentReferenceContext,
                document: editor.value.document,
            },
            hiveClient,
            {
                accept: ['url', 'anchor', 'page', 'space'],
            }
        );

        palette.open(contentRefPaletteDefinition);
        /**
         * Also return the definition if we want to use it in a command
         * trigger.
         */
        return contentRefPaletteDefinition;
    };

/**
 * Create callback to toggle an annotation.
 */
export const toggleAnnotation =
    ({ editor }: { editor: EditorContainer }) =>
    () => {
        editor.change((change) => {
            annotation.wrapWithAnnotation(change);
        });
    };

/**
 * Evaluate whether the currently selected text looks like a URL.
 */
function isSelectionUrlLike(value: Value): boolean {
    if (isBlockRangeSelection(value.selection) || value.isCollapsed) {
        return false;
    }

    const selectionText = value.fragment.getText();

    if (!selectionText) {
        return false;
    }

    return isUrlLike(selectionText);
}
